/**
 * TransactionHistory Component
 *
 * Displays categorized transaction history for a specific wallet.
 * Fetches transactions from the Crypto API and provides filtering
 * by category, direction, and status. Clicking a row opens the detail modal.
 *
 * @component
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ITEMS_PER_PAGE,
  TRANSACTION_CATEGORIES,
  TRANSACTION_DIRECTIONS,
  TRANSACTION_STATUSES,
} from "./constants";
import { commonStyles, colors, paginationStyles, buttonStyles } from "./styles";
import {
  formatDate,
  formatAmount,
  truncateText,
  getHashscanUrl,
  getCategoryBadgeStyle,
  getDirectionBadgeStyle,
  getStatusBadgeStyle,
  calculatePaginationRange,
} from "./utils";
import TransactionDetail from "./TransactionDetail";
import { getRequest } from "../../utils/httpRequest";

// ============================================================================
// Sub-components
// ============================================================================
const TransactionFilters = ({
  categoryFilter,
  onCategoryChange,
  directionFilter,
  onDirectionChange,
  statusFilter,
  onStatusChange,
  searchQuery,
  onSearchChange,
  totalShowing,
  totalFiltered,
}) => {
  const selectStyle = {
    padding: "8px 12px",
    paddingRight: "36px",
    border: `1px solid ${colors.gray400}`,
    borderRadius: "4px",
    fontSize: "13px",
    background: colors.white,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    backgroundSize: "12px",
    cursor: "pointer",
    minWidth: "130px",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
  };

  return (
    <div style={commonStyles.filterBar}>
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search by transaction ID, account, currency, memo..."
        style={{ ...commonStyles.input, maxWidth: "320px" }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <select
          value={categoryFilter}
          onChange={(e) => onCategoryChange(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Categories</option>
          {Object.entries(TRANSACTION_CATEGORIES)
            .filter(([key]) => key !== "ALL")
            .map(([key, value]) => (
              <option key={key} value={value}>
                {value}
              </option>
            ))}
        </select>
        <select
          value={directionFilter}
          onChange={(e) => onDirectionChange(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Directions</option>
          <option value="incoming">↓ Incoming</option>
          <option value="outgoing">↑ Outgoing</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => onStatusChange(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Statuses</option>
          <option value="success">Success</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
        <span
          style={{
            ...commonStyles.badge,
            background: colors.accent,
            color: colors.white,
          }}
        >
          {totalShowing} of {totalFiltered}
        </span>
      </div>
    </div>
  );
};

/**
 * TransactionTable - Table displaying transaction records
 */
/**
 * Extract fee from transaction metadata (following reference code pattern)
 */
const getTransactionFee = (tx) => {
  const metadata = tx.metadata;
  if (!metadata) return 0;
  if (metadata.swapDetails?.fee)
    return parseFloat(metadata.swapDetails.fee) || 0;
  if (metadata.fee) return parseFloat(metadata.fee) || 0;
  return 0;
};

const TransactionTable = ({
  transactions,
  hederaNetwork,
  onViewDetail,
  getCurrencyDisplay,
}) => (
  <div style={{ ...commonStyles.panel, marginBottom: 0 }}>
    <div className="index_table_container">
      <table className="index_table">
        <thead>
          <tr>
            <th className="col">Transaction ID/Hash</th>
            <th className="col">Category</th>
            <th className="col">Direction</th>
            <th className="col">Currency</th>
            <th className="col">Amount</th>
            <th className="col">Fees</th>
            <th className="col">Total</th>
            <th className="col">Status</th>
            <th className="col">Created At</th>
            <th className="col">Updated At</th>
            <th className="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx, idx) => {
            // Check if this is a settlement transaction
            const isSettlement =
              tx.isSettlement === true ||
              tx.type === "settlement" ||
              tx.transactionType === "SETTLEMENT";

            // For settlement, set category to "Settlement" if not already set
            const category =
              isSettlement && !tx.category ? "Settlement" : tx.category;

            const categoryStyle = getCategoryBadgeStyle(category);
            const statusStyle = getStatusBadgeStyle(tx.status);

            // For settlement transactions, use different field names
            const transactionId = isSettlement ? tx.id : tx.transactionId;
            const fee = isSettlement
              ? parseFloat(tx.fee) || 0
              : getTransactionFee(tx);
            const amount = parseFloat(tx.amount) || 0;
            const total = isSettlement
              ? parseFloat(tx.totalDeducted) || 0
              : amount + fee;
            const createdAt = isSettlement ? tx.timestamp : tx.createdAt;
            const updatedAt = isSettlement ? null : tx.updatedAt;

            return (
              <tr
                key={transactionId || idx}
                className={idx % 2 === 0 ? "even" : "odd"}
              >
                <td className="col">
                  {transactionId ? (
                    isSettlement ? (
                      <span title={transactionId}>
                        {truncateText(transactionId, 20)}
                      </span>
                    ) : (
                      <a
                        href={getHashscanUrl(transactionId, hederaNetwork)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tx-link"
                        onClick={(e) => e.stopPropagation()}
                        title={transactionId}
                      >
                        {truncateText(transactionId, 20)}
                      </a>
                    )
                  ) : isSettlement ? (
                    "Prediction Settlement"
                  ) : (
                    "-"
                  )}
                </td>
                <td className="col">
                  <span className="category-badge" style={categoryStyle}>
                    {category || "-"}
                  </span>
                </td>
                <td className="col">
                  <span className={`direction-badge ${tx.direction || ""}`}>
                    {tx.direction === "incoming" ? "↓ In" : "↑ Out"}
                  </span>
                </td>
                <td className="col">
                  {tx.tokenId ? getCurrencyDisplay(tx.tokenId) : "-"}
                </td>
                <td className="col">
                  <strong>{formatAmount(tx.amount)}</strong>
                </td>
                <td className="col">{fee > 0 ? formatAmount(fee) : "-"}</td>
                <td className="col">
                  <strong>{formatAmount(total)}</strong>
                </td>
                <td className="col">
                  <span className="status_tag" style={statusStyle}>
                    {tx.status || "-"}
                  </span>
                </td>
                <td className="col">{formatDate(createdAt)}</td>
                <td className="col">
                  {updatedAt ? formatDate(updatedAt) : "-"}
                </td>
                <td className="col">
                  <button
                    type="button"
                    onClick={() => onViewDetail(tx)}
                    style={{
                      ...buttonStyles.viewHistory,
                      padding: "6px 12px",
                      fontSize: "12px",
                    }}
                    onMouseOver={(e) =>
                      Object.assign(
                        e.currentTarget.style,
                        buttonStyles.viewHistoryHover,
                      )
                    }
                    onMouseOut={(e) =>
                      Object.assign(
                        e.currentTarget.style,
                        buttonStyles.viewHistoryDefault,
                      )
                    }
                  >
                    View Details
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * TransactionPagination - Pagination for transaction list
 */
const TransactionPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
}) => {
  if (totalPages <= 1) return null;

  const pages = calculatePaginationRange(currentPage, totalPages);
  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  return (
    <div style={paginationStyles.container}>
      <span style={paginationStyles.info}>
        Showing {startItem}-{endItem} of {totalItems} transactions
      </span>
      <div style={paginationStyles.nav}>
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            ...paginationStyles.navButton,
            background: currentPage === 1 ? colors.gray300 : colors.primary,
            color: currentPage === 1 ? colors.gray500 : colors.white,
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
          }}
        >
          ← Prev
        </button>
        {pages.map((page) => (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            style={{
              ...paginationStyles.pageButton,
              background: page === currentPage ? colors.accent : colors.white,
              color: page === currentPage ? colors.white : colors.gray700,
              border:
                page === currentPage ? "none" : `1px solid ${colors.border}`,
              fontWeight: page === currentPage ? 700 : 400,
            }}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            ...paginationStyles.navButton,
            background:
              currentPage === totalPages ? colors.gray300 : colors.primary,
            color: currentPage === totalPages ? colors.gray500 : colors.white,
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

/**
 * TransactionStats - Summary statistics for loaded transactions
 */
const TransactionStats = ({ transactions }) => {
  const stats = useMemo(() => {
    const deposits = transactions.filter(
      (t) => t.category === "Deposit",
    ).length;
    const withdrawals = transactions.filter(
      (t) => t.category === "Withdrawal",
    ).length;
    const internalTransfers = transactions.filter(
      (t) =>
        t.category === "Internal Transfer" ||
        t.memo?.startsWith("Internal Transfer"),
    ).length;
    const tokenSwaps = transactions.filter(
      (t) => t.category === "Token Swap",
    ).length;
    const currencySwaps = transactions.filter(
      (t) => t.category === "Currency Swap",
    ).length;
    const airdrops = transactions.filter(
      (t) => t.category === "Airdrop",
    ).length;
    const settlements = transactions.filter(
      (t) =>
        t.category === "Settlement" ||
        t.isSettlement === true ||
        t.type === "settlement" ||
        t.transactionType === "SETTLEMENT",
    ).length;

    return {
      deposits,
      withdrawals,
      internalTransfers,
      tokenSwaps,
      currencySwaps,
      airdrops,
      settlements,
      total: transactions.length,
    };
  }, [transactions]);

  const statCards = [
    {
      value: stats.total,
      label: "Total",
      gradient: `linear-gradient(135deg, #2e3b61 0%, #3d4d7a 100%)`,
      tooltip: "Total number of all transactions in this wallet",
    },
    {
      value: stats.deposits,
      label: "Deposits",
      gradient: `linear-gradient(135deg, #28a745 0%, #218838 100%)`,
      tooltip: "Incoming funds added to the wallet",
    },
    {
      value: stats.withdrawals,
      label: "Withdrawals",
      gradient: `linear-gradient(135deg, #dc3545 0%, #c82333 100%)`,
      tooltip: "Outgoing funds removed from the wallet",
    },
    {
      value: stats.internalTransfers,
      label: "Transfers",
      gradient: `linear-gradient(135deg, #6c3483 0%, #5b2c6f 100%)`,
      tooltip: "Internal transfers between user accounts",
    },
    {
      value: stats.tokenSwaps,
      label: "Token Swaps",
      gradient: `linear-gradient(135deg, #17a2b8 0%, #138496 100%)`,
      tooltip: "Cryptocurrency token exchange transactions",
    },
    {
      value: stats.airdrops,
      label: "Airdrops",
      gradient: `linear-gradient(135deg, #0c5460 0%, #0a3d44 100%)`,
      tooltip: "Free token distributions received by users",
    },
    {
      value: stats.settlements,
      label: "Settlements",
      gradient: `linear-gradient(135deg, #ffc107 0%, #e0a800 100%)`,
      tooltip: "Prediction market settlement payouts and deductions",
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        gap: "16px",
        marginBottom: "20px",
        flexWrap: "wrap",
      }}
    >
      {statCards.map((card) => (
        <div
          key={card.label}
          title={card.tooltip}
          style={{
            flex: 1,
            minWidth: "120px",
            padding: "16px 20px",
            borderRadius: "6px",
            textAlign: "center",
            background: card.gradient,
            cursor: "help",
          }}
        >
          <span
            style={{
              display: "block",
              fontSize: "28px",
              fontWeight: 700,
              color: colors.white,
            }}
          >
            {card.value}
          </span>
          <span
            style={{
              fontSize: "12px",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.85)",
              fontWeight: 600,
              letterSpacing: "0.5px",
            }}
          >
            {card.label}
          </span>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const TransactionHistory = ({ wallet, hederaNetwork, onBack }) => {
  // State
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [currencies, setCurrencies] = useState([]);

  // Filters (client-side on loaded data)
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Create a mapping from tokenId to currency info
  const tokenIdToCurrency = useMemo(() => {
    const map = new Map();
    currencies.forEach((currency) => {
      if (currency.tokenId) {
        map.set(currency.tokenId, {
          symbol: currency.symbol,
          name: currency.name,
        });
      }
    });
    return map;
  }, [currencies]);

  // Helper function to get currency display name
  const getCurrencyDisplay = useCallback(
    (tokenId) => {
      const currency = tokenIdToCurrency.get(tokenId);
      if (currency) {
        return currency.name || currency.symbol || tokenId;
      }
      return tokenId;
    },
    [tokenIdToCurrency],
  );

  // Fetch transactions via Rails proxy
  const fetchTransactions = useCallback(async () => {
    if (!wallet?.user_id) {
      setError("Missing user ID");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userExtId = wallet.user_id;
      const data = await getRequest(
        `/admin/agentic_payment_transaction_history/user_transactions?user_ext_id=${encodeURIComponent(
          userExtId,
        )}&limit=500&skip=0`,
      );
      const txList = data.transactions || [];
      setTransactions(txList);
      setTotalCount(data.count || txList.length);
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setError(err?.message || "Failed to load transactions");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [wallet]);

  // Fetch currencies from the backend
  const fetchCurrencies = useCallback(async () => {
    try {
      const data = await getRequest(
        "/admin/agentic_payment_airdrop/available_currencies",
      );
      if (data.success) {
        setCurrencies(data.data || []);
      }
    } catch (err) {
      console.error("Error fetching currencies:", err);
    }
  }, []);

  // Fetch on mount + when wallet changes
  useEffect(() => {
    fetchCurrencies();
    fetchTransactions();
  }, [fetchCurrencies, fetchTransactions]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, directionFilter, statusFilter, searchQuery]);

  // Filtered transactions (client-side)
  const filteredTransactions = useMemo(() => {
    let filtered = transactions;

    if (categoryFilter !== "all") {
      filtered = filtered.filter((tx) => {
        // Check if this is a settlement transaction
        const isSettlement =
          tx.isSettlement === true ||
          tx.type === "settlement" ||
          tx.transactionType === "SETTLEMENT";
        const category =
          isSettlement && !tx.category ? "Settlement" : tx.category;

        // Also match Internal Transfer by memo prefix (reference code pattern)
        if (categoryFilter === "Transfer") {
          return (
            category === "Internal Transfer" ||
            category === "Transfer" ||
            tx.memo?.startsWith("Internal Transfer")
          );
        }

        // Match settlement transactions
        if (categoryFilter === "Settlement") {
          return isSettlement || category === "Settlement";
        }

        return category === categoryFilter;
      });
    }

    if (directionFilter !== "all") {
      filtered = filtered.filter((tx) => tx.direction === directionFilter);
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (tx) => tx.status?.toLowerCase() === statusFilter,
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((tx) => {
        // Get currency display name for this transaction
        const currencyName = getCurrencyDisplay(tx.tokenId);
        const currencyNameLower = currencyName
          ? currencyName.toLowerCase()
          : "";

        // Check if this is a settlement transaction
        const isSettlement =
          tx.isSettlement === true ||
          tx.type === "settlement" ||
          tx.transactionType === "SETTLEMENT";
        const transactionId = isSettlement ? tx.id : tx.transactionId;

        return (
          transactionId?.toLowerCase().includes(query) ||
          tx.fromAccountId?.toLowerCase().includes(query) ||
          tx.toAccountId?.toLowerCase().includes(query) ||
          tx.tokenId?.toLowerCase().includes(query) ||
          tx.category?.toLowerCase().includes(query) ||
          tx.memo?.toLowerCase().includes(query) ||
          tx.offerId?.toLowerCase().includes(query) ||
          currencyNameLower.includes(query)
        );
      });
    }

    return filtered;
  }, [
    transactions,
    categoryFilter,
    directionFilter,
    statusFilter,
    searchQuery,
    getCurrencyDisplay,
  ]);

  // Pagination (client-side)
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredTransactions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTransactions, currentPage]);

  // Loading state
  if (loading) {
    return (
      <div>
        <div style={commonStyles.sectionHeader}>
          <div>
            <h2
              style={{
                margin: "0 0 4px 0",
                color: colors.white,
                fontSize: "20px",
                fontWeight: 600,
              }}
            >
              Transaction History
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {wallet.email}, {" "}
              <code style={{ color: "rgba(255,255,255,0.9)" }}>
                {wallet.address}
              </code>
            </p>
          </div>

          <button
            type="button"
            onClick={onBack}
            style={buttonStyles.ghost}
            onMouseOver={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostHover)
            }
            onMouseOut={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostDefault)
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Wallets
          </button>
        </div>
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: `3px solid ${colors.border}`,
              borderTopColor: colors.primary,
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p style={{ color: colors.gray600, fontSize: "14px" }}>
            Loading transaction history...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div>
        <div style={commonStyles.sectionHeader}>
          <div>
            <h2
              style={{
                margin: "0 0 4px 0",
                color: colors.white,
                fontSize: "20px",
                fontWeight: 600,
              }}
            >
              Transaction History
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {wallet.email}, {" "}
              <code style={{ color: "rgba(255,255,255,0.9)" }}>
                {wallet.address}
              </code>
            </p>
          </div>
          <button
            type="button"
            onClick={onBack}
            style={buttonStyles.ghost}
            onMouseOver={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostHover)
            }
            onMouseOut={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostDefault)
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Wallets
          </button>
        </div>
        <div
          className="flash flash_error"
          style={{ textAlign: "center", padding: "24px" }}
        >
          <p style={{ margin: "0 0 12px" }}>{error}</p>
          <button
            type="button"
            style={{
              ...commonStyles.button,
              ...commonStyles.buttonPrimary,
            }}
            onClick={fetchTransactions}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={commonStyles.sectionHeader}>
        <div>
          <h2
            style={{
              margin: "0 0 4px 0",
              color: colors.white,
              fontSize: "20px",
              fontWeight: 600,
            }}
          >
            Transaction History
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              color: "rgba(255,255,255,0.7)",
            }}
          >
            {wallet.email}, {" "}
            <code style={{ color: "rgba(255,255,255,0.9)" }}>
              {wallet.address}
            </code>
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            type="button"
            onClick={fetchTransactions}
            style={buttonStyles.ghost}
            onMouseOver={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostHover)
            }
            onMouseOut={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostDefault)
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Refresh
          </button>
          <button
            type="button"
            onClick={onBack}
            style={buttonStyles.ghost}
            onMouseOver={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostHover)
            }
            onMouseOut={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostDefault)
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Wallets
          </button>
        </div>
      </div>

      {/* Stats */}
      <TransactionStats transactions={transactions} />

      {/* Filters */}
      <TransactionFilters
        categoryFilter={categoryFilter}
        onCategoryChange={setCategoryFilter}
        directionFilter={directionFilter}
        onDirectionChange={setDirectionFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        totalShowing={paginatedTransactions.length}
        totalFiltered={filteredTransactions.length}
      />

      {/* Content */}
      {filteredTransactions.length === 0 ? (
        <div className="blank_slate" style={{ textAlign: "center" }}>
          <p>
            {searchQuery ||
            categoryFilter !== "all" ||
            directionFilter !== "all" ||
            statusFilter !== "all"
              ? "No transactions match your filters"
              : "No transactions found for this wallet"}
          </p>
        </div>
      ) : (
        <>
          <TransactionTable
            transactions={paginatedTransactions}
            hederaNetwork={hederaNetwork}
            onViewDetail={setSelectedTransaction}
            getCurrencyDisplay={getCurrencyDisplay}
          />
          <TransactionPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredTransactions.length}
          />
        </>
      )}

      {/* Detail Modal */}
      {selectedTransaction && (
        <TransactionDetail
          transaction={selectedTransaction}
          wallet={wallet}
          hederaNetwork={hederaNetwork}
          onClose={() => setSelectedTransaction(null)}
          getCurrencyDisplay={getCurrencyDisplay}
        />
      )}
    </div>
  );
};

export default TransactionHistory;
